import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { getSpaceEndpointUrl } from '@/api/services/serverless/space';
import { spacePointsService, type SpacePointsSearch } from '@/api/services/serverless/space-points';
import { validateParams } from '@/router/utils';

export const Route = createFileRoute(
  '/_authenticated/_dashboard/accounts/$accountId/serverless-spaces/$spaceId/_tabs/collections/$collectionName',
)({
  // Not the create form's name rule: a collection created through the API may use characters the form refuses.
  params: validateParams(
    z.object({
      collectionName: z.string().min(1),
    }),
  ),
  beforeLoad: async ({ context: { queryClient, spaceQuery, getCollectionQuery }, matches, params }) => {
    // No endpoint, no data plane: the Collections tab says the space is not ready yet.
    const { space } = await queryClient.ensureQueryData(spaceQuery);
    const baseUrl = getSpaceEndpointUrl(space);
    if (!baseUrl) {
      throw redirect({ to: '/accounts/$accountId/serverless-spaces/$spaceId/collections', params, replace: true });
    }

    // A bare collection URL opens its points.
    if (matches.at(-1)?.routeId === Route.id) {
      throw redirect({
        to: '/accounts/$accountId/serverless-spaces/$spaceId/collections/$collectionName/points',
        params,
        replace: true,
      });
    }

    const { accountId, spaceId, collectionName } = params;
    const ids = { accountId, spaceId, baseUrl, collectionName };
    return {
      baseUrl,
      collectionQuery: getCollectionQuery(baseUrl, collectionName),
      // Per search: the points browser filters and ranks them.
      listPointsQuery: (search: SpacePointsSearch) => spacePointsService.listPointsQuery({ ...ids, ...search }),
    };
  },
  // Not awaited: the layout shows a skeleton while the collection loads.
  loader: ({ context: { queryClient, collectionQuery } }) => void queryClient.prefetchQuery(collectionQuery),
  component: lazyRouteComponent(
    () => import('@/components/Serverless/SpaceCollections/SpaceCollectionLayout'),
    'SpaceCollectionLayout',
  ),
});
